import React, { useEffect, useRef, useState } from 'react';
import { Card } from 'antd';
import { Line } from '@antv/g2plot';
import styles from './styles/chart-placeholder.module.scss';

const DirectDepositSwitchesChart = ({ title, data }) => {
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(550);

    useEffect(() => {
        // Function to get container width with padding consideration
        const updateWidth = () => {
            if (chartRef.current) {
                // Get the actual rendered width of the chart div
                const width = chartRef.current.getBoundingClientRect().width || chartRef.current.offsetWidth || 520;
                if (width > 0) {
                    setContainerWidth(Math.max(width, 300)); // Minimum width of 300px
                }
            }
        };

        // Initial width calculation with a small delay to ensure DOM is ready
        const timeout = setTimeout(updateWidth, 100);

        // ResizeObserver to watch for chart div size changes
        // Use requestAnimationFrame to prevent ResizeObserver loop errors
        let rafId = null;
        const resizeObserver = new ResizeObserver((entries) => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            rafId = requestAnimationFrame(() => {
                for (let entry of entries) {
                    // Use the actual rendered width from getBoundingClientRect for accuracy
                    const width = entry.target.getBoundingClientRect().width || entry.contentRect.width || 520;
                    if (width > 0) {
                        setContainerWidth(Math.max(width, 300)); // Minimum width of 300px
                    }
                }
                rafId = null;
            });
        });

        // Observe the chart div itself - it will resize when its container resizes
        const chartElement = chartRef.current;
        if (chartElement) {
            resizeObserver.observe(chartElement);
        }

        return () => {
            clearTimeout(timeout);
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        // Use provided data or fallback to mock data
        const chartData = data || [
            { date: 'January', switches: 1000 },
            { date: 'February', switches: 3000 },
            { date: 'March', switches: 1500 },
            { date: 'April', switches: 2500 },
            { date: 'May', switches: 4500 },
            { date: 'June', switches: 5500 },
            { date: 'July', switches: 6500 }
        ];

        if (chartRef.current && containerWidth > 0) {
            // Get the actual available width to ensure chart fits
            const actualWidth = Math.min(containerWidth, chartRef.current.getBoundingClientRect().width || containerWidth);

            // If chart already exists, update it instead of recreating
            if (chartInstanceRef.current) {
                chartInstanceRef.current.update({
                    width: actualWidth,
                    data: chartData
                });
            } else {
                // Create new chart instance
                chartInstanceRef.current = new Line(chartRef.current, {
                    data: chartData,
                    xField: 'date',
                    yField: 'switches',
                    point: {
                        size: 4,
                        shape: 'circle'
                    },
                    line: {
                        color: '#1890ff',
                        size: 2
                    },
                    tooltip: {
                        formatter: (datum) => {
                            return { name: 'Switches', value: datum.switches ? datum.switches.toLocaleString() : '0' };
                        }
                    },
                    yAxis: {
                        min: 0
                    },
                    height: 200,
                    width: actualWidth,
                    smooth: false
                });

                chartInstanceRef.current.render();
            }
        }

        // Cleanup function
        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
                chartInstanceRef.current = null;
            }
        };
    }, [data, containerWidth]);

    return (
        <Card className={styles.chartPlaceholder} title={title}>
            <div className={styles.chartContainer} ref={containerRef}>
                <div className={styles.chartSubtitle}>Number of direct deposit switches made during onboarding over time.</div>
                <div ref={chartRef} style={{ width: '100%', height: '200px' }} />
            </div>
        </Card>
    );
};

export default DirectDepositSwitchesChart;
